import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface LogoGallery {
  id: string;
  brand_id: string;
  name: string;
  description?: string;
  is_published: boolean;
  display_order: number;
  created_at: string;
  updated_at: string;
}

export function useLogoGalleries(brandId: string | undefined) {
  const queryClient = useQueryClient();

  const { data: galleries, isLoading } = useQuery({
    queryKey: ['logo-galleries', brandId],
    queryFn: async () => {
      if (!brandId) return [];
      
      const { data, error } = await supabase
        .from('brand_logo_galleries')
        .select('*')
        .eq('brand_id', brandId)
        .order('display_order', { ascending: true })
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as LogoGallery[];
    },
    enabled: !!brandId,
  });

  const createMutation = useMutation({
    mutationFn: async (gallery: Omit<LogoGallery, 'id' | 'created_at' | 'updated_at'>) => {
      const { data, error } = await supabase
        .from('brand_logo_galleries')
        .insert([gallery])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['logo-galleries', brandId] });
      toast.success('Gallery created successfully');
    },
    onError: (error: any) => {
      toast.error(`Failed to create gallery: ${error.message}`);
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, ...updates }: Partial<LogoGallery> & { id: string }) => {
      const { data, error } = await supabase
        .from('brand_logo_galleries')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['logo-galleries', brandId] });
      toast.success('Gallery updated successfully');
    },
    onError: (error: any) => {
      toast.error(`Failed to update gallery: ${error.message}`);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('brand_logo_galleries')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['logo-galleries', brandId] });
      toast.success('Gallery deleted successfully');
    },
    onError: (error: any) => {
      toast.error(`Failed to delete gallery: ${error.message}`);
    },
  });

  return {
    galleries: galleries || [],
    isLoading,
    createGallery: createMutation.mutateAsync,
    updateGallery: updateMutation.mutateAsync,
    deleteGallery: deleteMutation.mutateAsync,
  };
}
